import React, { useRef, useState, useEffect } from "react";
import { useStore } from "../App/store";
import { IoClose, IoHome } from "react-icons/io5";
import { shallow } from "zustand/shallow";

interface TabNavigatorProps {
  className?: string;
}

const TabNavigator: React.FC<TabNavigatorProps> = ({ className }) => {
  const {
    drilldownTabs,
    activeDrilldownTab,
    setActiveDrilldownTab,
    closeDrilldownTab,
    reorderDrilldownTabs,
  } = useStore(
    (state) => ({
      drilldownTabs: state.drilldownTabs,
      activeDrilldownTab: state.activeDrilldownTab,
      setActiveDrilldownTab: state.setActiveDrilldownTab,
      closeDrilldownTab: state.closeDrilldownTab,
      reorderDrilldownTabs: state.reorderDrilldownTabs,
    }),
    shallow,
  );

  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

  // Keyboard navigation: Ctrl+Tab to cycle tabs
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.key === "Tab") {
        e.preventDefault();
        const allTabs = [null, ...drilldownTabs.map((t) => t.id)];
        const currentIndex = allTabs.indexOf(activeDrilldownTab);
        const nextIndex = (currentIndex + 1) % allTabs.length;
        setActiveDrilldownTab(allTabs[nextIndex]);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [activeDrilldownTab, drilldownTabs, setActiveDrilldownTab]);

  const handleMainTabClick = () => {
    setActiveDrilldownTab(null);
  };

  const handleTabClick = (tabId: string) => {
    setActiveDrilldownTab(tabId);
  };

  const handleCloseTab = (e: React.MouseEvent, tabId: string) => {
    e.stopPropagation();
    closeDrilldownTab(tabId);
  };

  const handleDragStart = (e: React.DragEvent, index: number) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (draggedIndex !== null && draggedIndex !== index) {
      setDragOverIndex(index);
    }
  };

  const handleDrop = (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    if (draggedIndex !== null && draggedIndex !== dropIndex) {
      reorderDrilldownTabs(draggedIndex, dropIndex);
    }
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  return (
    <div className={`tab-navigator ${className || ""}`}>
      {/* Main tab - always present */}
      <div
        className={`tab-item main-tab ${activeDrilldownTab === null ? "active" : ""}`}
        onClick={handleMainTabClick}
      >
        <IoHome className="tab-icon" />
        <span className="tab-label">Main</span>
      </div>

      {/* Drill-down tabs */}
      {drilldownTabs.map((tab, index) => (
        <div
          key={tab.id}
          className={`tab-item ${activeDrilldownTab === tab.id ? "active" : ""} ${
            draggedIndex === index ? "dragging" : ""
          } ${dragOverIndex === index ? "drag-over" : ""}`}
          draggable
          onDragStart={(e) => handleDragStart(e, index)}
          onDragOver={(e) => handleDragOver(e, index)}
          onDrop={(e) => handleDrop(e, index)}
          onDragEnd={handleDragEnd}
          onClick={() => handleTabClick(tab.id)}
        >
          <span className="tab-label">{tab.label || "Untitled"}</span>
          <button
            className="tab-close"
            onClick={(e) => handleCloseTab(e, tab.id)}
            aria-label="Close tab"
          >
            <IoClose />
          </button>
        </div>
      ))}
    </div>
  );
};

export default TabNavigator;
